import type { MarketFlowData } from '../types/market'

interface SectorMetrics {
  maxPositiveFlow: number
  minNegativeFlow: number
  maxAbsFlow: number
  frameCount: number
}

type RankedSector = [string, SectorMetrics]

function collectMetrics(source: MarketFlowData): RankedSector[] {
  const metricsById = new Map<string, SectorMetrics>()
  for (const frame of source.frames) {
    for (const sector of frame.sectors) {
      const metrics = metricsById.get(sector.id) ?? {
        maxPositiveFlow: 0,
        minNegativeFlow: 0,
        maxAbsFlow: 0,
        frameCount: 0,
      }
      metrics.maxPositiveFlow = Math.max(metrics.maxPositiveFlow, sector.flow)
      metrics.minNegativeFlow = Math.min(metrics.minNegativeFlow, sector.flow)
      metrics.maxAbsFlow = Math.max(metrics.maxAbsFlow, Math.abs(sector.flow))
      metrics.frameCount += 1
      metricsById.set(sector.id, metrics)
    }
  }
  return [...metricsById].filter(([, metrics]) => metrics.frameCount === source.frames.length)
}

function applySelection(source: MarketFlowData, selectedIds: string[]): MarketFlowData {
  return {
    ...source,
    frames: source.frames.map((frame) => {
      const byId = new Map(frame.sectors.map((sector) => [sector.id, sector]))
      return {
        ...frame,
        sectors: selectedIds.map((id) => byId.get(id)).filter((sector) => sector !== undefined),
      }
    }),
  }
}

export function selectFixedSectors(source: MarketFlowData, limit = 45): MarketFlowData {
  if (!Number.isInteger(limit) || limit < 1) throw new Error('板块数量必须是正整数')

  const candidates = collectMetrics(source)
  const positiveTarget = Math.floor(limit / 2)
  const negativeTarget = limit - positiveTarget
  const positive = candidates
    .filter(([, metrics]) => metrics.maxPositiveFlow > 0)
    .sort(([leftId, left], [rightId, right]) =>
      right.maxPositiveFlow - left.maxPositiveFlow || leftId.localeCompare(rightId))
  const negative = candidates
    .filter(([, metrics]) => metrics.minNegativeFlow < 0)
    .sort(([leftId, left], [rightId, right]) =>
      left.minNegativeFlow - right.minNegativeFlow || leftId.localeCompare(rightId))

  const selectedIds: string[] = []
  const selectedSet = new Set<string>()
  const take = (ranked: typeof candidates, count: number) => {
    for (const [id] of ranked) {
      if (selectedIds.length >= limit || count <= 0) break
      if (selectedSet.has(id)) continue
      selectedIds.push(id)
      selectedSet.add(id)
      count -= 1
    }
  }

  take(positive, positiveTarget)
  take(negative, negativeTarget)
  if (selectedIds.length < limit) {
    const remaining = candidates.sort(([leftId, left], [rightId, right]) =>
      right.maxAbsFlow - left.maxAbsFlow || leftId.localeCompare(rightId))
    take(remaining, limit - selectedIds.length)
  }

  return applySelection(source, selectedIds)
}

export function selectRealFixedSectors(source: MarketFlowData): MarketFlowData {
  const selectedIds = collectMetrics(source)
    .filter(([id]) => id.startsWith('eastmoney:industry:'))
    .sort(([leftId, left], [rightId, right]) =>
      right.maxAbsFlow - left.maxAbsFlow || leftId.localeCompare(rightId))
    .slice(0, 36)
    .map(([id]) => id)

  return applySelection(source, selectedIds)
}
